import { useState } from 'react';
import { BackHeader } from '../components/BackHeader';
import { 
  Sparkles, CheckCircle, Clock, AlertCircle, Calendar,
  Camera, MessageSquare, User, ChevronRight, RefreshCw,
  CheckSquare, Trash2, Droplet, Wind, Sun, Plus, Users
} from 'lucide-react';

// Staff member type from Staff Directory
interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  role: 'cleaning' | 'maintenance' | 'security' | 'gardener' | 'maid' | 'admin' | 'manager' | 'chef' | 'concierge' | 'coordinator';
  status: 'active' | 'inactive' | 'on_leave';
  hourlyRate: number;
  rating: number;
}

interface Turnover {
  id: string;
  propertyName: string;
  checkOut: string;
  checkIn: string;
  hoursAvailable: number;
  status: 'pending' | 'in-progress' | 'completed' | 'ready';
  cleanerId?: string;
  cleanerName?: string;
  checklist: ChecklistItem[];
  photos: { before: string[]; after: string[] };
  suppliesNeeded: string[];
  notes: string;
}

interface ChecklistItem {
  id: string;
  category: 'bedroom' | 'bathroom' | 'kitchen' | 'living' | 'general';
  task: string;
  completed: boolean;
}

export default function TurnoverCoordination() {
  // Connected staff from Staff Directory (cleaning crew)
  const [availableCleaners] = useState<StaffMember[]>([]);

  const [turnovers, setTurnovers] = useState<Turnover[]>([]);

  const [selectedTurnover, setSelectedTurnover] = useState<Turnover | null>(null);
  const [activeTab, setActiveTab] = useState<'checklist' | 'photos' | 'supplies' | 'assign'>('checklist');
  const [showAssignModal, setShowAssignModal] = useState(false);

  const assignCleaner = (turnoverId: string, cleaner: StaffMember) => {
    setTurnovers(turnovers.map(t => 
      t.id === turnoverId 
        ? { ...t, cleanerId: cleaner.id, cleanerName: `${cleaner.firstName} ${cleaner.lastName}`, status: 'in-progress' as const }
        : t
    ));
    if (selectedTurnover && selectedTurnover.id === turnoverId) {
      setSelectedTurnover({ 
        ...selectedTurnover, 
        cleanerId: cleaner.id, 
        cleanerName: `${cleaner.firstName} ${cleaner.lastName}`,
        status: 'in-progress'
      });
    }
    setShowAssignModal(false);
  };

  const toggleChecklistItem = (turnoverId: string, itemId: string) => {
    setTurnovers(turnovers.map(t => {
      if (t.id === turnoverId) {
        const newChecklist = t.checklist.map(item => 
          item.id === itemId ? { ...item, completed: !item.completed } : item
        );
        const allCompleted = newChecklist.every(item => item.completed);
        return { 
          ...t, 
          checklist: newChecklist,
          status: allCompleted ? 'completed' as const : t.status
        };
      }
      return t;
    }));
    if (selectedTurnover) {
      const updated = turnovers.find(t => t.id === turnoverId);
      if (updated) setSelectedTurnover(updated);
    }
  };

  const getProgress = (checklist: ChecklistItem[]) => {
    const completed = checklist.filter(item => item.completed).length;
    return Math.round((completed / checklist.length) * 100);
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      'in-progress': 'bg-blue-500/20 text-blue-400',
      completed: 'bg-green-500/20 text-green-400',
      ready: 'bg-purple-500/20 text-purple-400',
    };
    const labels = {
      pending: 'Pendiente',
      'in-progress': 'En Progreso',
      completed: 'Completado',
      ready: 'Listo para Check-in',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const checklistByCategory = (checklist: ChecklistItem[]) => {
    const categories = {
      bedroom: { label: 'Dormitorios', icon: Sun },
      bathroom: { label: 'Baños', icon: Droplet },
      kitchen: { label: 'Cocina', icon: CheckSquare },
      living: { label: 'Sala/Estar', icon: Wind },
      general: { label: 'General', icon: Sparkles },
    };
    
    return Object.entries(categories).map(([key, cat]) => ({
      ...cat,
      items: checklist.filter(item => item.category === key),
    })).filter(cat => cat.items.length > 0);
  };

  return (
    <div className="space-y-6">
      <BackHeader fallback="/" />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Coordinación de Turnos</h2>
          <p className="text-[var(--v-text-secondary)]">Gestiona limpiezas entre check-out y check-in</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowAssignModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            Asignar Limpieza
          </button>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Sincronizar Calendario
          </button>
        </div>
      </div>

      {/* Turnovers List */}
      <div className="space-y-4">
        {turnovers.length === 0 && (
          <div className="rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-10 text-center">
            <p className="font-semibold text-[var(--v-text-primary)]">No hay rotaciones programadas</p>
            <p className="mt-1 text-sm text-[var(--v-text-secondary)]">Las rotaciones de limpieza entre check-out y check-in aparecerán aquí cuando tengas propiedades en renta con reservas activas.</p>
          </div>
        )}
        {turnovers.map((turnover) => {
          const progress = getProgress(turnover.checklist);
          const assignedCleaner = availableCleaners.find(c => c.id === turnover.cleanerId);
          
          return (
            <div
              key={turnover.id}
              onClick={() => setSelectedTurnover(turnover)}
              className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)] hover:border-blue-500/50 cursor-pointer transition"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">{turnover.propertyName}</h3>
                    {getStatusBadge(turnover.status)}
                  </div>
                  
                  <div className="flex items-center gap-6 text-sm text-[var(--v-text-secondary)] mb-3">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Check-out: {turnover.checkOut}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Check-in: {turnover.checkIn}
                    </span>
                    <span className="flex items-center gap-1 text-blue-400">
                      <Clock className="w-4 h-4" />
                      {turnover.hoursAvailable}h disponibles
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="text-[var(--v-text-secondary)] text-sm">
                      {assignedCleaner ? (
                        <span className="text-green-400">
                          {turnover.cleanerName} ★{assignedCleaner.rating}
                        </span>
                      ) : (
                        <span className="text-yellow-400">Sin asignar</span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-3xl font-bold text-[var(--v-text-primary)]">{progress}%</div>
                  <div className="w-24 h-2 bg-slate-700 rounded-full mt-2 overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${progress === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-slate-500 text-xs mt-1">
                    {turnover.checklist.filter(i => i.completed).length}/{turnover.checklist.length} tareas
                  </p>
                </div>
              </div>

              {turnover.notes && (
                <div className="mt-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <p className="text-yellow-400 text-sm">{turnover.notes}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Assign Cleaner Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-slate-900/95 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] w-full max-w-md">
            <div className="p-4 border-b border-[var(--v-surface-3)] flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">Asignar Equipo de Limpieza</h3>
              <button onClick={() => setShowAssignModal(false)} className="text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]">✕</button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-[var(--v-text-secondary)] text-sm">Selecciona un miembro del equipo de limpieza:</p>
              {availableCleaners.map((cleaner) => (
                <button
                  key={cleaner.id}
                  onClick={() => {
                    // Assign to first pending turnover or create logic to select which one
                    const pendingTurnover = turnovers.find(t => t.status === 'pending');
                    if (pendingTurnover) {
                      assignCleaner(pendingTurnover.id, cleaner);
                    }
                  }}
                  className="w-full p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 text-left flex items-center justify-between"
                >
                  <div>
                    <p className="text-white font-medium">{cleaner.firstName} {cleaner.lastName}</p>
                    <p className="text-[var(--v-text-secondary)] text-sm">${cleaner.hourlyRate}/h • {cleaner.status}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-yellow-400 font-medium">★{cleaner.rating}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedTurnover && (
        <div className="fixed inset-0 bg-slate-900/95 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-4 border-b border-[var(--v-surface-3)] flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-[var(--v-text-primary)]">{selectedTurnover.propertyName}</h3>
                <p className="text-[var(--v-text-secondary)] text-sm">Turno #{selectedTurnover.id}</p>
              </div>
              <button 
                onClick={() => setSelectedTurnover(null)}
                className="text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]"
              >
                ✕
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-[var(--v-surface-3)]">
              {['checklist', 'photos', 'supplies', 'assign'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as any)}
                  className={`flex-1 py-3 text-sm font-medium capitalize ${
                    activeTab === tab 
                      ? 'text-blue-400 border-b-2 border-blue-400' 
                      : 'text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'
                  }`}
                >
                  {tab === 'checklist' ? 'Checklist' : tab === 'photos' ? 'Fotos' : tab === 'supplies' ? 'Suministros' : 'Asignar'}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="p-4">
              {activeTab === 'checklist' && (
                <div className="space-y-4">
                  {checklistByCategory(selectedTurnover.checklist).map((category) => (
                    <div key={category.label}>
                      <h4 className="font-semibold text-[var(--v-text-primary)] mb-2 flex items-center gap-2">
                        <category.icon className="w-4 h-4 text-blue-400" />
                        {category.label}
                      </h4>
                      <div className="space-y-2">
                        {category.items.map((item) => (
                          <label
                            key={item.id}
                            className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700"
                          >
                            <input
                              type="checkbox"
                              checked={item.completed}
                              onChange={() => toggleChecklistItem(selectedTurnover.id, item.id)}
                              className="w-5 h-5 rounded border-slate-500 text-blue-600 focus:ring-blue-500"
                            />
                            <span className={`${item.completed ? 'text-slate-500 line-through' : 'text-white'}`}>
                              {item.task}
                            </span>
                            {item.completed && (
                              <CheckCircle className="w-4 h-4 text-green-400 ml-auto" />
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'photos' && (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-[var(--v-text-primary)] mb-3">Fotos Antes</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <button className="aspect-square bg-slate-700 border-2 border-dashed border-slate-600 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-slate-500">
                        <Camera className="w-8 h-8 mb-1" />
                        <span className="text-xs">Agregar</span>
                      </button>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[var(--v-text-primary)] mb-3">Fotos Después</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <button className="aspect-square bg-slate-700 border-2 border-dashed border-slate-600 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-slate-500">
                        <Camera className="w-8 h-8 mb-1" />
                        <span className="text-xs">Agregar</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'supplies' && (
                <div>
                  <h4 className="font-semibold text-[var(--v-text-primary)] mb-3">Suministros Necesarios</h4>
                  <div className="space-y-2">
                    {selectedTurnover.suppliesNeeded.map((supply, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                        <CheckSquare className="w-5 h-5 text-[var(--v-text-secondary)]" />
                        <span className="text-white flex-1">{supply}</span>
                        <button className="text-green-400 hover:text-green-300 text-sm">
                          <CheckCircle className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    <button className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg flex items-center justify-center gap-2">
                      <Plus className="w-4 h-4" />
                      Agregar suministro
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'assign' && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-[var(--v-text-primary)] mb-3">Equipo de Limpieza Disponible</h4>
                  {availableCleaners.map((cleaner) => (
                    <button
                      key={cleaner.id}
                      onClick={() => assignCleaner(selectedTurnover.id, cleaner)}
                      className={`w-full p-4 rounded-lg text-left flex items-center justify-between ${
                        selectedTurnover.cleanerId === cleaner.id 
                          ? 'bg-green-500/20 border border-green-500/30' 
                          : 'bg-slate-700/50 hover:bg-slate-700'
                      }`}
                    >
                      <div>
                        <p className="text-white font-medium">{cleaner.firstName} {cleaner.lastName}</p>
                        <p className="text-[var(--v-text-secondary)] text-sm">${cleaner.hourlyRate}/h • {cleaner.status}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-yellow-400 font-medium">★{cleaner.rating}</span>
                        {selectedTurnover.cleanerId === cleaner.id && (
                          <p className="text-green-400 text-xs mt-1">Asignado</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-[var(--v-surface-3)] flex gap-3">
              <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Contactar Limpieza
              </button>
              {getProgress(selectedTurnover.checklist) === 100 && (
                <button className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Marcar Listo
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
